import { useContext, useEffect, useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import Session from 'supertokens-auth-react/recipe/session';
import { DateTime } from 'luxon';
import { ApiContext, AuthContext, UserContext } from '../App';
import UserRecord from '../records/user';
import SuperTokensConfig from '../lib/constants/SuperTokensConfig';
import { resetPostHog } from '../lib/utils/posthog';

const needsRefresh = (token) => {
  if(!token) return;

  const expires = DateTime.fromSeconds(token.created_at)
    .plus({ hours: 2 })
    .setZone(DateTime.local().zoneName);

  return DateTime.now().setZone(DateTime.local().zoneName) >= expires;
};

export const useCreateToken = () => {
  const api = useContext(ApiContext);

  return useMutation({
    mutationFn: (contact) => {
      api.post('/api/users/reports/verify', {
        type: 'email',
        contact: contact.contact,
      });
    },
    onError: (error) => console.log(error),
  });
};

export const useVerifyToken = () => {
  const api = useContext(ApiContext);

  return useMutation({
    mutationFn: (payload) => api.post('/api/users/reports/verified', payload),
  });
};

export const useRefreshToken = () => {
  const api = useContext(ApiContext);
  const saved = JSON.parse(window.localStorage.getItem('auth'));

  return useMutation({
    mutationFn: async (token) => {
      return await api.post('/oauth/token', {
        grant_type: 'refresh_token',
        refresh_token: token.refresh_token,
      });
    },
    onSuccess: async (res) => {
      window.localStorage.setItem(
        'auth',
        JSON.stringify({ ...saved, token: res.data }),
      );
      window.location.reload();
    },
    onError: (error) => {
      console.log(error);
    },
    onSettled: () => {
      window.location.reload();
    },
    retry: false
  });
};

export const useSetAuth = (data, setLoading, signout) => {
  const auth = useContext(AuthContext);
  const user = useContext(UserContext);

  data = { ...data, signout };

  useEffect(() => {
    if (!auth.auth && data && data.authorized && !user.user) {
      auth.setAuth(data);
      user.setUser(new UserRecord({ ...data.user }));
      window.localStorage.setItem('auth', JSON.stringify(data));
      setLoading && setLoading(false);
    }
  }, [data]);

  return data;
};

export const useHydrateAuth = () => {
  const { data, mutate } = useRefreshToken();
  const [loading, setLoading] = useState(false);

  const saved = JSON.parse(window.localStorage.getItem('auth'));
  if (saved && needsRefresh(saved.token) && !loading) {
    setLoading(true);
    mutate(saved.token);
  }

  const signin = () => redirectToLogin();
  const signout = () => {
    window.localStorage.removeItem('auth');
    resetPostHog();
    redirectToLogin();
  };

  const auth = useSetAuth(data || saved, setLoading, signout);

  const isLoading = () => loading;

  const isAuthenticated = () => auth?.authorized === true;

  const redirectToLogin = () => (window.location.href = '/login');

  const accessToken = () => auth?.token?.access_token;

  return {
    isLoading,
    isAuthenticated,
    redirectToLogin,
    accessToken,
    signin,
    signout,
  };
};

export const useSuperTokensAuth = () => {
  const [loading, setLoading] = useState(true);
  const [sessionExists, setSessionExists] = useState(false);

  const authCtx = useContext(AuthContext);
  const userCtx = useContext(UserContext);

  const buildContext = useCallback(async () => {
    if (await Session.doesSessionExist()) {
      setSessionExists(true);

      const userId = await Session.getUserId();
      const accessToken = await Session.getAccessToken();
      const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();

      authCtx.setAuth({
        accessToken,
        accessTokenPayload,
        signin,
        signout,
      });

      userCtx.setUser({
        id: userId,
        roles: accessTokenPayload['st-role']?.v || [],
        permissions: accessTokenPayload['st-perm']?.v || [],
      });
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    buildContext();
  }, [buildContext]);

  const isLoading = () => loading;

  const isAuthenticated = () => sessionExists;

  const isAuthorized = (roles) => {
    if (userCtx.user.roles.includes('superadmin')) return true; // always allow superadmins
    for (const role of userCtx.user.roles) {
      if (roles.includes(role)) {
        return true;
      }
    }
    return false;
  };

  const signin = () => {
    redirectToLogin();
  };

  const signout = async () => {
    resetPostHog();
    try {
      await Session.signOut();
      redirectToLogin();
    } catch (error) {
      redirectToLogin();
    }
  };

  const redirectToLogin = () => {
    const currentUrl = window.location.origin + window.location.pathname;
    const loginUrl = new URL(
      SuperTokensConfig.appInfo.websiteDomain +
        SuperTokensConfig.appInfo.websiteBasePath,
    );
    loginUrl.searchParams.set('r', currentUrl);
    window.location.href = loginUrl;
  };

  const accessToken = () => authCtx.auth?.accessToken;

  return {
    isLoading,
    isAuthorized,
    isAuthenticated,
    redirectToLogin,
    accessToken,
    signin,
    signout,
  };
};
